import { isEmpty } from 'ramda';
import { useContext } from 'react';
import { TEDLRKonteringsForslagFI_REGISTRERASEnum } from '../../../../../../../../../../globalQueryTypes';
import { inDateInterval } from '../../../../../../../../../../Utils/utils';
import { LoginStateContext } from '../../../../../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import {
  IFieldValidation,
  IValidateFn,
  mergeBusinessDataFormValues,
  parseText,
  IFieldValidations,
  createFormData,
} from '../../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import KonteringContext from '../../../../../../../../_Common/Components/KonteringManager/KonteringContext';
import { KonteringUtils } from '../../../../../../../../_Common/Components/KonteringManager/KonteringManagerUtils';
import { IKontodelTillaggsinfo } from '../../../../../../../../_Common/Components/KonteringManager/Query/KonteringManagerQuery';
import { FakturaattestWrapperContext } from '../../../../../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import {
  FakturaDelQuery_node_TEDLRFakturaDel,
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All,
} from '../../../../../Queries/_QueryTypes/FakturaDelQuery';
import {
  ButtonTypeEnum,
  getAktuellFakturaAttest,
  getMinAttestTyp,
} from '../../../../../Utils/FakturadelUtils';
import { isFieldError } from '../../../Utils/KonteringUtils';

export enum Tillaggsinfokod {
  INV11 = 'INV11',
  INV12 = 'INV12',
  INV11PI = 'INV11PI',
  INV12PI = 'INV12PI',
  FI = 'FI',
}

export const tillaggsinfoIsKomponent = (
  tillaggsinfo: IKontodelTillaggsinfo
): boolean =>
  tillaggsinfo.kod === Tillaggsinfokod.INV11 ||
  tillaggsinfo.kod === Tillaggsinfokod.INV11PI;

export const isForbrukningsInventarie = (
  tillaggsinfo: IKontodelTillaggsinfo
): boolean => tillaggsinfo.kod === Tillaggsinfokod.FI;

export const isTillaggsinfoError = (error: IFieldValidation) =>
  isFieldError(error) &&
  error.fieldName.startsWith(tillagsinfoFieldErrorPrefix);

export const getTillaggsinfoValidationErrorMessageFn =
  (errors: IFieldValidations, konteringsforslagId: string) =>
  (fieldName: string, kontodelOrdningsnummer?: number): string | undefined => {
    let validationMessage: string | undefined;

    for (let error of errors) {
      if (typeof error === 'object') {
        const kontodelOrdningsnummerString =
          kontodelOrdningsnummer != null ? ` ${kontodelOrdningsnummer}` : '';
        if (
          error.fieldName ===
          `${tillagsinfoFieldErrorPrefix} ${konteringsforslagId} ${fieldName}${kontodelOrdningsnummerString}`
        ) {
          validationMessage = error.message;
          break;
        }
      }
    }

    return validationMessage;
  };

const getTillaggsinfoValue = (
  fieldName: string,
  updatedKonteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All,
  kontodelOrdningsnummer?: number
) => {
  let value = null;

  const isNewBuilding = Boolean(updatedKonteringsforslag.AR_NY);

  if (fieldName === 'AR_KOMPONENT_ID') {
    value = updatedKonteringsforslag._Associations?.Komponenttyp?.id;
  } else if (fieldName === 'AR_TILLGANG' && !isNewBuilding) {
    value = updatedKonteringsforslag._Associations?.Tillgang?.id;
  } else if (fieldName === 'AR_OMRADE_ID' && isNewBuilding) {
    value = updatedKonteringsforslag._Associations?.Omrade?.id;
  } else if (fieldName === 'AR_OMRADE_ID' && !isNewBuilding) {
    value =
      updatedKonteringsforslag._Associations?.Tillgang?._Associations?.Plats
        ?.id;
  } else if (fieldName === 'AR_AVSKR_KDEL' && isNewBuilding) {
    value = (
      updatedKonteringsforslag._Associations as unknown as
        | Record<string, { id: string } | undefined>
        | undefined
    )?.[`AvskrKontodel${kontodelOrdningsnummer}`]?.id;
  } else if (fieldName === 'AR_FI_ANLKAT_ID') {
    value = updatedKonteringsforslag._Associations?.FIKategori?.id;
  } else if (fieldName === 'AR_FI_OMRADE_ID') {
    value = updatedKonteringsforslag._Associations?.FIOmrade?.id;
  } else if (fieldName === 'AR_FI_BYGGNAD_ID') {
    value = updatedKonteringsforslag._Associations?.FIByggnad?.id;
  } else {
    value = (updatedKonteringsforslag as unknown as Record<string, unknown>)[
      fieldName
    ];
  }
  return value;
};
export const tillagsinfoFieldErrorPrefix = '$tillaggsinfo$';

const isAdditionalInfoEmpty = (
  updatedKonteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All,
  tillaggsinfo: IKontodelTillaggsinfo,
  isNewBuilding: boolean,
  tillaggsinfoAvskrivningKontodelOrdningsnr: number[]
): IFieldValidation[] => {
  const fields = tillaggsinfo.falt;

  const isEmpty = fields.every(field => {
    const fieldName =
      field.fieldName === 'AR_TILLGANG' && isNewBuilding
        ? 'AR_TILLGANG_BEN'
        : field.fieldName;

    if (fieldName === 'AR_AVSKR_KDEL') {
      if (isNewBuilding) {
        const isAvskrKdelEmpty =
          tillaggsinfoAvskrivningKontodelOrdningsnr.every(ordningsnummer => {
            const value = getTillaggsinfoValue(
              fieldName,
              updatedKonteringsforslag,
              ordningsnummer
            );

            return value == null || value === '';
          });

        return isAvskrKdelEmpty;
      }
    }
    const fieldValue = getTillaggsinfoValue(
      fieldName,
      updatedKonteringsforslag
    );

    return fieldValue == null || fieldValue === '';
  });

  if (isEmpty) {
    return [
      {
        fieldName: `${tillagsinfoFieldErrorPrefix} ${updatedKonteringsforslag.id}`,
        message: `Tilläggsinformation saknas`,
        type: 'Warning',
      },
    ];
  }
  return [];
};

const validateTillaggsinfo = (
  updatedKonteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All,
  tillaggsinfo: IKontodelTillaggsinfo,
  konteringUtils: KonteringUtils,
  kontoplanId: string,
  bokfDatum: Date,
  isFIActive: boolean
): IFieldValidations => {
  const requiredFields = tillaggsinfo.falt.filter(field => field.obligatorisk);
  let errors: IFieldValidations = [];
  const isNewBuilding = Boolean(updatedKonteringsforslag.AR_NY);

  const isForbrukning = isForbrukningsInventarie(tillaggsinfo);

  if (isForbrukning && !isFIActive) {
    return [];
  }

  const registerInventarium =
    (updatedKonteringsforslag?.FI_REGISTRERAS ??
      TEDLRKonteringsForslagFI_REGISTRERASEnum.EJ_ANGIVET) ===
    TEDLRKonteringsForslagFI_REGISTRERASEnum.JA;

  const notChosenRegisterInventarium =
    (updatedKonteringsforslag?.FI_REGISTRERAS ??
      TEDLRKonteringsForslagFI_REGISTRERASEnum.EJ_ANGIVET) ===
    TEDLRKonteringsForslagFI_REGISTRERASEnum.EJ_ANGIVET;

  if (!registerInventarium && isForbrukning && isFIActive) {
    if (notChosenRegisterInventarium) {
      return [
        {
          fieldName: `${tillagsinfoFieldErrorPrefix} ${updatedKonteringsforslag.id} `,
          message: `Måste ange om du vill registerföra förbrukningsinventariet`,
          type: 'Error',
        },
      ];
    }
    return errors;
  }

  const tillaggsinfoAvskrivningKontodelOrdningsnr =
    konteringUtils.getTillaggsinfoAvskrivningKontodelOrdningsnummerList(
      bokfDatum
    );

  requiredFields.forEach(requiredField => {
    const fieldName =
      requiredField.fieldName === 'AR_TILLGANG' && isNewBuilding
        ? 'AR_TILLGANG_BEN'
        : requiredField.fieldName;

    if (fieldName === 'AR_AVSKR_KDEL') {
      if (isNewBuilding) {
        tillaggsinfoAvskrivningKontodelOrdningsnr.forEach(ordningsnummer => {
          const value = getTillaggsinfoValue(
            fieldName,
            updatedKonteringsforslag,
            ordningsnummer
          );

          const kontodelsDef = konteringUtils.getKontodelDefByOrdningsNr(
            kontoplanId,
            ordningsnummer
          );

          //Det finns åtminstone en koddel som är aktiv i kontodelsdefinitonen
          if (
            kontodelsDef?.kontodelar.some(
              kontodel =>
                kontodel.aktiv &&
                inDateInterval(
                  kontodel.franDatum,
                  kontodel.tillDatum,
                  bokfDatum
                )
            )
          ) {
            const fieldDisplayName = kontodelsDef?.namn;

            if (value == null || value === '') {
              errors.push({
                fieldName: `${tillagsinfoFieldErrorPrefix} ${updatedKonteringsforslag.id} ${fieldName} ${ordningsnummer}`,
                message: `${fieldDisplayName} måste anges`,
                type: 'Error',
              });
            }
          }
        });
      }
    } else {
      const value = getTillaggsinfoValue(fieldName, updatedKonteringsforslag);

      if (value == null || value === '') {
        errors.push({
          fieldName: `${tillagsinfoFieldErrorPrefix} ${updatedKonteringsforslag.id} ${fieldName}`,
          message: `${requiredField.benamning} måste anges`,
          type: 'Error',
        });
      }
    }
  });

  const missingErrors = isAdditionalInfoEmpty(
    updatedKonteringsforslag,
    tillaggsinfo,
    isNewBuilding,
    tillaggsinfoAvskrivningKontodelOrdningsnr
  );

  errors = errors.concat(missingErrors);

  return errors;
};

export const useValidateFakturadelTillaggsinfo = (
  isFIActive: boolean,
  showWarning?: boolean
): IValidateFn => {
  const konteringContext = useContext(KonteringContext);
  const reservForContext = useContext(FakturaattestWrapperContext);
  const loginStateContext = useContext(LoginStateContext);

  return ({ values, data }) => {
    const formData = createFormData(values, data);
    let errors: IFieldValidations = [];

    if (
      isEmpty(formData) ||
      !loginStateContext.isLoggedIn ||
      konteringContext.konteringValidationQueryLoading
    ) {
      return errors;
    }

    const updatedValues = mergeBusinessDataFormValues(
      parseText(formData),
      parseText(values)
    );

    const minAttestTyp = formData
      ? getMinAttestTyp(formData as FakturaDelQuery_node_TEDLRFakturaDel)
      : null;

    const minFakturaAttest = getAktuellFakturaAttest(
      updatedValues?._Associations?.Fakturaattest ?? [],
      reservForContext.reservForData,
      loginStateContext.sign,
      minAttestTyp,
      ButtonTypeEnum.ATERTA
    );

    const updatedKonteringsforslagList: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All[] =
      updatedValues?._Associations?.Faktura?._Associations?.Konteringsforslag?.filter(
        (kf: any) =>
          !values?._Associations?.Faktura_Values?._Associations?.Konteringsforslag_Delete_Values?.some(
            (deletedKf: any) => deletedKf.id === kf.id
          )
      );

    const bokfDatum = new Date(
      formData?._Associations?.Faktura?._Associations?.Registrering?._Associations?.Bokfverifikationer?.DATUM
    );

    if (Array.isArray(updatedKonteringsforslagList)) {
      updatedKonteringsforslagList
        .filter(kf => {
          return (
            kf?._Associations?.Fakturaattest?.id ===
            minFakturaAttest.aktuellFakturaAttest?.id
          );
        })
        .forEach(updatedKonteringsforslag => {
          const kodplanId: string | undefined =
            formData?._Associations?.Faktura?._Associations?.Registrering
              ?._Associations?.Bokfverifikationer?._Associations?.Kodplan?.id;
          const baskontoKontodelId: string | undefined =
            updatedKonteringsforslag?._Associations?.Kontodel1?.id;

          if (kodplanId && baskontoKontodelId) {
            const tillaggsinfo =
              konteringContext.konteringUtils.getKontodelTillaggsinfo(
                kodplanId,
                baskontoKontodelId
              );

            if (tillaggsinfo && showWarning) {
              const konteringsforslagErrors = validateTillaggsinfo(
                updatedKonteringsforslag,
                tillaggsinfo,
                konteringContext.konteringUtils,
                kodplanId,
                bokfDatum,
                isFIActive
              );
              errors = errors.concat(konteringsforslagErrors);
            }
          }
        });
    }

    return errors;
  };
};
